import { FC, useEffect, useState } from "react";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { Layout } from "../../containers/layout";
import { useToastNotification } from "../../hooks/useToastNotification/useToastNotification";
import { useParams } from "react-router-dom";
import { SignupUserDto } from "../../models/signupUserDto";
import { useSubmitUser } from "../../hooks/useSubmitUser/useSubmitUser";
import { AcceptInvitationStep } from "../../components/acceptInvitationStep/AcceptInvitationStep";
import { CompleteRegistrationStep } from "../../components/completeUserRegistrationStep/CompleteRegistrationStep";
import { ConfirmationStep } from "../../components/confirmationStep/ConfirmationStep";
import { TranslationKeyType } from "../../hooks/useTranslationHook/types";
import { useInvitationInformation } from "../../hooks/useInvitationInformation/useInvitationInformation";

import {
  InviteNewUserFlowProvider,
  useInviteNewUserFlow,
  InviteNewUserStepEnum
} from "../../contexts/inviteNewUserContext";
import { useInvitationChoice } from "../../hooks/useInvitationChoice/useInvitationChoice";
import { navigateSecurely } from "../../utils/navigation/navigationUtils";

type BackendErrorDetail = { code?: string; message?: string };

export const InviteNewUserPage: FC = () => {
  const { submitUser, loading: userLoading } = useSubmitUser();
  const { showNotification, NotificationElement } = useToastNotification();
  const { translateString } = useTranslation();

  const {
    workflowId,
    setWorkflowId,
    currentStep,
    setCurrentStep,
    resetFlow,
    setLanguage,
    setOrganizationId,
    organizationName,
    setOrganizationName,
    email,
    setEmail,
    relationType,
    setRelationType,
    invitationState,
    setInvitationState
  } = useInviteNewUserFlow();

  const { getInvitationInformationSafely } = useInvitationInformation();
  const { sendInvitationChoice } = useInvitationChoice();
  const { workflowId: workflowIdParam } = useParams();

  const [initializing, setInitializing] = useState<boolean>(true);

  useEffect(() => {
    const init = async () => {
      resetFlow();

      if (!workflowIdParam) {
        showNotification("error_invalid_invitation", "error");
        setInitializing(false);
        return;
      }

      setWorkflowId(workflowIdParam);

      const info = await getInvitationInformationSafely(workflowIdParam);
      if (!info) {
        setInitializing(false);
        return;
      }

      setOrganizationId(info.organizationId);
      setOrganizationName(info.organizationName);
      setLanguage(info.preferredLanguage ?? null);
      setEmail(info.email);
      setRelationType(
        info.relationType?.trim().toLowerCase() === "primary" ? "primary" : "external"
      );
      setInvitationState(info.invitationState);

      setCurrentStep(InviteNewUserStepEnum.AcceptOrRejectInvitation);
      setInitializing(false);
    };

    void init();
  }, [workflowIdParam]);

  const handleAccept = async () => {
    if (!workflowId) return;
    if (invitationState !== "Created") return;

    const res = await sendInvitationChoice(workflowId, "accepted");
    if (!res.ok) {
      const key = res.code as TranslationKeyType | undefined;
      const text = key ? translateString(key) : (res.message ?? "Error");
      showNotification(text, "error");
      return;
    }

    setCurrentStep(InviteNewUserStepEnum.CompleteRegistration);
  };

  const handleReject = async () => {
    if (!workflowId) {
      navigateSecurely();
      return;
    }

    if (invitationState !== "Created") return;
    const res = await sendInvitationChoice(workflowId, "rejected");
    navigateSecurely();
  };

  const handleUserSubmit = async (userDto: SignupUserDto) => {
    try {
      await submitUser(userDto);
      setCurrentStep(InviteNewUserStepEnum.RegistrationComplete);
    } catch (err: unknown) {
      const errors = Array.isArray(err) ? (err as BackendErrorDetail[]) : [];

      errors.forEach((e) => {
        const key = e.code as TranslationKeyType;
        const msg = (key ? translateString(key) : null) ?? e.message ?? "Error";
        showNotification(msg, "error");
      });
    }
  };

  const handleCancel = async (): Promise<void> => {
    navigateSecurely();
  };

  const handleReturnToLogin = () => {
    navigateSecurely();
  };

  const renderStep = () => {
    switch (currentStep) {
      case InviteNewUserStepEnum.AcceptOrRejectInvitation:
        return (
          <AcceptInvitationStep
            organizationName={organizationName || "—"}
            relationType={relationType || "-"}
            invitationState={invitationState || "-"}
            onAccept={handleAccept}
            onReject={handleReject}
          />
        );
      case InviteNewUserStepEnum.CompleteRegistration:
        return (
          <CompleteRegistrationStep
            email={email ?? ""}
            workflowId={workflowId ?? ""}
            languageOverride={null}
            onSubmit={handleUserSubmit}
            onCancel={handleCancel}
            loading={userLoading}
          />
        );
      case InviteNewUserStepEnum.RegistrationComplete:
        return (
          <ConfirmationStep
            cardTitle={translateString("registration_complete")}
            message={translateString("registration_complete_description")}
            buttonText={translateString("return_to_login")}
            onButtonClick={handleReturnToLogin}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      {initializing ? null : renderStep()}
      {NotificationElement}
    </Layout>
  );
};

export const InviteNewUserPageWithProvider: FC = () => (
  <InviteNewUserFlowProvider>
    <InviteNewUserPage />
  </InviteNewUserFlowProvider>
);
